import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lightning,
  SignOut,
  CaretLeft,
  ChartBar,
  Wallet,
  CreditCard,
  ChartLineUp,
  Buildings,
  Sparkle,
  Robot,
  Receipt,
  ChartPie,
  ShieldCheck,
  Car,
  Bank,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import ThemeToggle from "../components/ThemeToggle";
import CommandCenter from "../components/finance/CommandCenter";
import ScopeToggle from "../components/finance/ScopeToggle";
import {
  AccountsPanel,
  DebtsPanel,
  IncomesPanel,
  ExpensesPanel,
} from "../components/finance/EntityPanels";
import DocumentImporter from "../components/finance/DocumentImporter";
import CFOChat from "../components/finance/CFOChat";
import WakeWord from "../components/finance/WakeWord";
import VoiceQuickResponse from "../components/finance/VoiceQuickResponse";
import TransactionsPanel from "../components/finance/TransactionsPanel";
import ReportsPanel from "../components/finance/ReportsPanel";
import ReceivablesPanel from "../components/finance/ReceivablesPanel";
import BankConnectionsPanel from "../components/finance/BankConnectionsPanel";
import AlertsBell from "../components/finance/AlertsBell";
import DebtStrategy from "../components/finance/DebtStrategy";
import WhatIfLab from "../components/finance/WhatIfLab";

const TABS = [
  { key: "command", label: "Command Center", Icon: ChartBar },
  { key: "accounts", label: "Cuentas", Icon: Wallet },
  { key: "debts", label: "Deudas", Icon: CreditCard },
  { key: "receivables", label: "Por Cobrar", Icon: Car },
  { key: "banks", label: "Bancos", Icon: Bank },
  { key: "income", label: "Ingresos & Gastos", Icon: ChartLineUp },
  { key: "transactions", label: "Transacciones", Icon: Receipt },
  { key: "reports", label: "Reportes", Icon: ChartPie },
];

export default function Finance() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [scope, setScope] = useState("all");
  const [tab, setTab] = useState("command");
  const [metrics, setMetrics] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInitialSession, setChatInitialSession] = useState(null);
  // Voice wake-word quick response state
  const [voiceQuery, setVoiceQuery] = useState(null);
  // When user clicks a category tile in Reports, jump to Transactions
  // pre-filtered by that category.
  const [txInitialCategory, setTxInitialCategory] = useState(null);

  const handleWake = (command) => {
    const cmd = (command || "").trim();
    if (cmd.length > 3) {
      // User said "Hey CFO, cuánto me debe Sergio" — process directly
      setVoiceQuery({ question: cmd, id: Date.now() });
    } else {
      // Bare "Hey CFO" — open the chat drawer
      setChatOpen(true);
    }
  };

  const openChatFromVoice = (sessionId) => {
    setChatInitialSession(sessionId || null);
    setChatOpen(true);
    setVoiceQuery(null);
  };

  const jumpToTransactionsWithCategory = (categoryKey) => {
    setTxInitialCategory(categoryKey);
    setTab("transactions");
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [m, a, d, i, e] = await Promise.all([
        api.get(`/finance/dashboard?scope=${scope}`),
        api.get(`/finance/accounts?scope=${scope}`),
        api.get(`/finance/debts?scope=${scope}`),
        api.get(`/finance/incomes?scope=${scope}`),
        api.get(`/finance/expenses?scope=${scope}`),
      ]);
      setMetrics(m.data.metrics);
      setAccounts(a.data);
      setDebts(d.data);
      setIncomes(i.data);
      setExpenses(e.data);
    } catch (err) {
      toast.error("No se pudieron cargar tus finanzas");
    }
  }, [user, scope]);

  useEffect(() => {
    load();
  }, [load, reloadFlag]);

  const refresh = () => setReloadFlag((n) => n + 1);

  const handleSeedDemo = async () => {
    if (!window.confirm("Cargar datos de ejemplo (cuentas, deudas, ingresos y gastos)?")) return;
    setSeeding(true);
    try {
      await api.post("/finance/seed-demo");
      toast.success("Datos demo cargados");
      refresh();
    } catch (err) {
      toast.error("No se pudo cargar demo");
    } finally {
      setSeeding(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg">
        <div className="text-white/70 font-mono text-sm tracking-widest">
          LOADING<span className="animate-pulse">...</span>
        </div>
      </div>
    );
  }

  const empty = accounts.length === 0 && debts.length === 0 && incomes.length === 0 && expenses.length === 0;

  return (
    <div className="relative flex min-h-screen bg-deck-bg text-white">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside
        data-testid="finance-sidebar"
        className={`${collapsed ? "w-[72px]" : "w-[260px]"} shrink-0 bg-deck-sidebar border-r border-white/5 flex flex-col transition-all duration-300 z-10 relative`}
      >
        {/* Logo */}
        <div className="px-5 py-6 flex items-center gap-3 border-b border-white/5">
          <div className="w-10 h-10 rounded-2xl bg-[#10B981] flex items-center justify-center deck-tile shrink-0">
            <ChartBar size={22} weight="fill" className="text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#10B981]">PERSONAL CFO</div>
              <div className="text-base font-heading font-bold text-white truncate">Finanzas</div>
            </div>
          )}
        </div>

        {/* Workspace switcher */}
        <WorkspaceSwitcher collapsed={collapsed} />

        {/* Tabs */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
          {!collapsed && (
            <span className="px-2 pb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
              Módulos
            </span>
          )}
          {TABS.map((t) => {
            const Icon = t.Icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`fin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                title={t.label}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                  active
                    ? "bg-white/8 border border-white/10"
                    : "hover:bg-white/5 border border-transparent"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 deck-tile ${active ? "bg-[#10B981]" : "bg-white/6"}`}
                >
                  <Icon size={16} weight="fill" className="text-white" />
                </div>
                {!collapsed && (
                  <span className={`flex-1 truncate text-sm font-heading font-semibold ${active ? "text-white" : "text-white/60"}`}>
                    {t.label}
                  </span>
                )}
                {!collapsed && active && <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/5 p-3">
          {user?.is_admin && (
            <button
              data-testid="fin-admin-link"
              onClick={() => navigate("/admin")}
              title="Panel de administrador"
              className="w-full mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-300 text-[10px] font-mono uppercase tracking-[0.18em] font-bold transition"
            >
              <ShieldCheck size={12} weight="fill" className="shrink-0" />
              {!collapsed && <span>Admin Console</span>}
            </button>
          )}
          <div className="flex items-center gap-3 p-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-white/10" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <ChartBar size={16} className="text-white/60" />
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-heading font-semibold truncate">{user?.name}</div>
                <div className="text-[10px] font-mono text-white/40 truncate">{user?.email}</div>
              </div>
            )}
            {!collapsed && (
              <button
                data-testid="fin-logout-btn"
                onClick={handleLogout}
                title="Cerrar sesión"
                className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition"
              >
                <SignOut size={14} weight="bold" className="text-white/60" />
              </button>
            )}
          </div>
          <button
            data-testid="fin-sidebar-toggle"
            onClick={() => setCollapsed((v) => !v)}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white/70 text-[10px] font-mono uppercase tracking-[0.18em] transition"
          >
            <CaretLeft size={12} weight="bold" className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
            {!collapsed && "Contraer"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-[1]">
        {/* Topbar */}
        <header
          data-testid="finance-topbar"
          className="px-6 md:px-10 py-5 flex items-center gap-3 border-b border-white/5 bg-deck-bg/60 backdrop-blur-md sticky top-0 z-20"
        >
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#10B981]">
              PERSONAL CFO · {TABS.find((t) => t.key === tab)?.label.toUpperCase()}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-black text-white tracking-tight truncate">
              {tab === "command"
                ? "Command Center"
                : tab === "accounts"
                ? "Cuentas"
                : tab === "debts"
                ? "Deudas"
                : tab === "receivables"
                ? "Por Cobrar"
                : tab === "banks"
                ? "Bancos"
                : tab === "transactions"
                ? "Transacciones"
                : tab === "reports"
                ? "Reportes"
                : "Ingresos & Gastos"}
            </h1>
          </div>
          <div className="flex-1" />

          <ScopeToggle value={scope} onChange={setScope} testId="fin-scope" />

          {/* Alerts bell */}
          <AlertsBell />

          {/* CFO Chat */}
          <WakeWord onWake={handleWake} />
          <button
            data-testid="open-chat-btn"
            onClick={() => setChatOpen(true)}
            title="Pregunta a tu CFO"
            className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white font-heading font-bold px-4 py-2.5 text-sm transition deck-tile"
          >
            <Robot size={14} weight="fill" />
            <span className="hidden md:inline">Pregunta al CFO</span>
          </button>

          {empty && (
            <button
              data-testid="seed-demo-btn"
              onClick={handleSeedDemo}
              disabled={seeding}
              className="flex items-center gap-2 rounded-xl bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-40 text-black font-heading font-bold px-4 py-2.5 text-sm transition"
              title="Cargar datos demo"
            >
              <Sparkle size={14} weight="fill" />
              <span className="hidden md:inline">{seeding ? "Cargando..." : "Cargar demo"}</span>
            </button>
          )}

          <ThemeToggle />
        </header>

        <main className="flex-1 px-6 md:px-10 py-6 md:py-10 overflow-auto">
          {tab === "command" && (
            <>
              {empty ? (
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#10B981]/20 border border-[#10B981]/30 flex items-center justify-center mb-4">
                    <ChartBar size={28} weight="fill" className="text-[#10B981]" />
                  </div>
                  <div className="text-2xl font-heading font-bold mb-2">
                    Tu CFO está listo, pero necesita datos.
                  </div>
                  <div className="text-white/50 mb-8 font-mono text-sm max-w-md">
                    Agrega tu primera cuenta, deuda, ingreso o gasto — o carga
                    datos de ejemplo para explorar.
                  </div>
                  <div className="flex gap-3">
                    <button
                      data-testid="empty-add-account"
                      onClick={() => setTab("accounts")}
                      className="px-5 py-3 rounded-xl bg-[#2563EB] text-white font-heading font-bold deck-tile"
                    >
                      Agregar cuenta
                    </button>
                    <button
                      data-testid="empty-seed-demo"
                      onClick={handleSeedDemo}
                      disabled={seeding}
                      className="px-5 py-3 rounded-xl bg-[#FACC15] text-black font-heading font-bold deck-tile disabled:opacity-40"
                    >
                      {seeding ? "Cargando..." : "Cargar demo"}
                    </button>
                  </div>
                </div>
              ) : (
                <CommandCenter metrics={metrics} scope={scope} />
              )}
            </>
          )}

          {tab === "accounts" && (
            <AccountsPanel accounts={accounts} scope={scope} onChange={refresh} />
          )}

          {tab === "debts" && (
            <div className="space-y-4">
              <DebtsPanel debts={debts} scope={scope} onChange={refresh} />
              <DebtStrategy scope={scope} />
              <WhatIfLab scope={scope} />
            </div>
          )}

          {tab === "receivables" && (
            <ReceivablesPanel />
          )}

          {tab === "banks" && (
            <BankConnectionsPanel />
          )}

          {tab === "income" && (
            <div className="space-y-4">
              <DocumentImporter scope={scope} onImported={refresh} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <IncomesPanel incomes={incomes} scope={scope} onChange={refresh} />
                <ExpensesPanel expenses={expenses} scope={scope} onChange={refresh} />
              </div>
            </div>
          )}

          {tab === "transactions" && (
            <TransactionsPanel
              scope={scope}
              onDataChanged={refresh}
              initialCategory={txInitialCategory}
              onInitialCategoryConsumed={() => setTxInitialCategory(null)}
            />
          )}

          {tab === "reports" && (
            <ReportsPanel scope={scope} onCategoryClick={jumpToTransactionsWithCategory} />
          )}
        </main>
      </div>

      {/* CFO Chat overlay */}
      <CFOChat
        open={chatOpen}
        onClose={() => { setChatOpen(false); setChatInitialSession(null); }}
        scope={scope}
        initialSessionId={chatInitialSession}
      />
      {voiceQuery && (
        <VoiceQuickResponse
          question={voiceQuery.question}
          scope={scope}
          onClose={() => setVoiceQuery(null)}
          onOpenChat={openChatFromVoice}
        />
      )}
    </div>
  );
}
